// Ported from obsidian-plugin/src/components/ProgressPipeline.tsx.
// No Obsidian dependencies — straight copy.
import React from 'react';
import type { PipelineStep, StepDetail } from '../../types';

interface ProgressPipelineProps {
  step: PipelineStep | null | undefined;
  completedSteps?: PipelineStep[];
  stepDetails?: StepDetail[];
}

const STEPS: { key: PipelineStep; label: string }[] = [
  { key: 'planning', label: 'Plan' },
  { key: 'retrieving', label: 'Search' },
  { key: 'reranking', label: 'Rerank' },
  { key: 'generating', label: 'Generate' },
];

const STEP_ORDER: Record<PipelineStep, number> = {
  planning: 0,
  retrieving: 1,
  reranking: 2,
  generating: 3,
};

export function StepNode({ status }: { status: 'done' | 'active' | 'pending' }) {
  if (status === 'done') {
    return (
      <svg className="malpedia-pipeline-node-svg" viewBox="0 0 14 14" aria-hidden="true">
        <circle cx="7" cy="7" r="6.25" fill="currentColor" />
        <path
          d="M4 7.2 L6.2 9.2 L10 5.2"
          fill="none"
          stroke="var(--background-primary)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (status === 'active') {
    return (
      <>
        <span className="malpedia-pipeline-node-pulse" aria-hidden="true" />
        <svg className="malpedia-pipeline-node-svg" viewBox="0 0 14 14" aria-hidden="true">
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      </>
    );
  }
  return (
    <svg className="malpedia-pipeline-node-svg" viewBox="0 0 14 14" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function ProgressPipeline({
  step,
  completedSteps,
  stepDetails,
}: ProgressPipelineProps) {
  const completedSet = new Set(completedSteps || []);
  const hasCompleted = completedSet.size > 0;

  if (step == null && !hasCompleted) return null;

  const activeIdx = step != null ? STEP_ORDER[step] : -1;

  // When streaming has ended (step == null), infer the highest completed
  // step index so that all steps up to and including it show as done.
  // This fixes the bug where 'planning' showed ○ after completion because
  // completedSteps might not contain 'planning' for non-planned modes.
  const maxCompletedIdx = hasCompleted
    ? Math.max(...[...completedSet].map((s) => STEP_ORDER[s as PipelineStep] ?? -1))
    : -1;

  const stepTooltips: Partial<Record<PipelineStep, string>> = {};
  if (stepDetails?.length) {
    for (const d of stepDetails) {
      const clean = d.message.replace(/\.{2,}$/, '').trimEnd();
      stepTooltips[d.step as PipelineStep] = d.count != null ? `${clean} (${d.count})` : clean;
    }
  }

  const visibleSteps = step != null || hasCompleted ? STEPS : [];
  const doneThreshold = step != null ? activeIdx : maxCompletedIdx + 1;
  const phase: string = step ?? 'completed';

  return (
    <div className="malpedia-progress-pipeline" data-phase={phase}>
      <div className="malpedia-pipeline-steps">
        {visibleSteps.map(({ key, label }, idx) => {
          const stepIdx = STEP_ORDER[key];
          const isDone = step != null ? stepIdx < activeIdx : stepIdx <= maxCompletedIdx;
          const isActive = step != null && stepIdx === activeIdx;
          const status: 'done' | 'active' | 'pending' = isDone ? 'done' : isActive ? 'active' : 'pending';
          // Connector to the right of this step is "filled" when this step is done.
          const connectorFilled = stepIdx < doneThreshold;
          return (
            <React.Fragment key={key}>
              <span
                className={`malpedia-pipeline-step ${status} malpedia-pipeline-step--${key}`}
                title={stepTooltips[key]}
              >
                <span className="malpedia-pipeline-node">
                  <StepNode status={status} />
                </span>
                <span className="malpedia-pipeline-label">{label}</span>
              </span>
              {idx < visibleSteps.length - 1 && (
                <span
                  className={`malpedia-pipeline-connector malpedia-pipeline-connector--${key}`}
                  data-filled={connectorFilled ? 'true' : 'false'}
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
